import {
  Select,
  LegacyStack,
  Button,
  FormLayout,
  Labelled,
  TextField,
  Collapsible,
  BlockStack,
  RadioButton,
  ButtonGroup,
  Checkbox,
  InlineStack,
  Box,
  Text,
  Tooltip,
  Label,
} from "@shopify/polaris";
import React, { useState, useCallback } from "react";
import PopoverWithCheckList from "../../common/Filters/PopoverWithCheckList";
import { useShopifyChannelSalesOptions } from "../../../api_utils/requests";
import { KEYS } from "./utils";
import type { Choice, Option } from "../../../api_utils/types";
import { humanDateFormat, prettyFormatDate, softAssertNumber } from "../../../helper_functions/utils";
import { ChevronDownIcon } from "@shopify/polaris-icons";

const samplingPeriods = [
  { label: "Past 30 Days", value: "30", key: "30" },
  { label: "Past 90 Days", value: "90", key: "90" },
  { label: "Past 180 Days", value: "180", key: "180" },
  { label: "Past 365 Days", value: "365", key: "365" },
];

const forecastingMethods: Choice[] = [
  { label: "Sales rate", value: "sales_rate", helpText: "Best for products with consistent sales" },
  { label: "Seasonal", value: "seasonal", helpText: "Best for seasonal products" },
];

type ForecastingMethod = "sales_rate" | "seasonal";

function ForecastingForm({
  daysOfInventory,
  handleFormFieldChange,
  salesChannelIds,
  selectedForecastingMethod,
  selectedSamplingPeriod,
  includeAllOutOfStockVariants,
  arrivalDate,
}: {
  readonly daysOfInventory: number;
  readonly handleFormFieldChange: (
    key: (typeof KEYS)[keyof typeof KEYS],
    value: ForecastingMethod | number | "Any" | number[] | boolean,
  ) => void;
  readonly salesChannelIds: number[];
  readonly selectedForecastingMethod: string;
  readonly selectedSamplingPeriod: number;
  readonly includeAllOutOfStockVariants: boolean;
  readonly arrivalDate: Date,
}): React.ReactElement {
  const { data: salesShopifyChannels } = useShopifyChannelSalesOptions();

  const handleForecastingModelChange = useCallback(
    ([forecasting_method]: ForecastingMethod[]): void => {
      handleFormFieldChange(KEYS.FORECASTING_METHOD, forecasting_method);
    },
    [handleFormFieldChange],
  );
  const handleSamplingPeriodChange = useCallback(
    (sampling_period: string): void => {
      handleFormFieldChange(KEYS.SAMPLING_PERIOD, Number(sampling_period));
    },
    [handleFormFieldChange],
  );
  const handleSalesSourceSelection = useCallback(
    (shopify_channel_ids: string[]): void => {
      handleFormFieldChange(KEYS.SALES_CHANNEL_IDS, shopify_channel_ids.map(softAssertNumber));
    },
    [handleFormFieldChange],
  );
  const removeShopifyChannel = useCallback(
    (removedSalesChannelId: string): void => {
      const remaining = salesChannelIds.filter((salesChannelId) => salesChannelId.toString() !== removedSalesChannelId);
      handleFormFieldChange(KEYS.SALES_CHANNEL_IDS, remaining);
    },
    [handleFormFieldChange, salesChannelIds],
  );
  const [daysOfInventoryError, setDaysOfInventoryError] = useState<string>();
  const handleDaysOfInventoryChange = useCallback(
    (newDaysOfInventory: number): void => {
      if (newDaysOfInventory > 365 || newDaysOfInventory < 1) {
        setDaysOfInventoryError("Days of inventory must be between 1 and 365 days.");
      } else {
        setDaysOfInventoryError(undefined);
      }
      handleFormFieldChange(KEYS.DAYS_OF_INVENTORY, newDaysOfInventory);
    },
    [handleFormFieldChange],
  );

  const stockoutDate: Date | undefined = daysOfInventory ? new Date(arrivalDate.getTime() + daysOfInventory * 24 * 60 * 60 * 1000) : undefined;
  const samplingPeriodStartLastYear: Date | undefined = daysOfInventory ? new Date(arrivalDate.getTime() - 365 * 24 * 60 * 60 * 1000) : undefined;
  const samplingPeriodEndLastYear: Date | undefined = stockoutDate ? new Date(stockoutDate.getTime() - 365 * 24 * 60 * 60 * 1000) : undefined;

  const [collapsibleOpen, setCollapsibleOpen] = useState(false);

  const selectedPeriod = samplingPeriods.find((item) => item.value === selectedSamplingPeriod.toString());

  const handleIncludeAllOutOfStockVariantsChange = useCallback(() => {
    handleFormFieldChange(KEYS.INCLUDE_ALL_OUT_OF_STOCK_VARIANTS, !includeAllOutOfStockVariants);
  }, [includeAllOutOfStockVariants, handleFormFieldChange]);

  return (
    <LegacyStack vertical>
      <FormLayout>
        <FormLayout.Group title="Select a forecasting model">
          <ButtonGroup gap="loose">
            {forecastingMethods.map((method) => (
              <RadioButton
                checked={selectedForecastingMethod === method.value}
                helpText={method.helpText}
                key={method.value}
                label={method.label}
                name="forecasting-method"
                onChange={() => {
                  handleForecastingModelChange([method.value as ForecastingMethod]);
                }}
              />
            ))}
          </ButtonGroup>
        </FormLayout.Group>

        {selectedForecastingMethod !== "seasonal" && (
          <div style={{ maxWidth: "264px" }}>
            <Select
              helpText="How many days of sales to use."
              label="Sampling period"
              onChange={handleSamplingPeriodChange}
              options={samplingPeriods satisfies Option[]}
              value={selectedPeriod.value.toString()}
            />
          </div>
        )}
        <InlineStack gap="400">
          <div style={{ maxWidth: "164px" }}>
            <TextField
              align="right"
              autoComplete="off"
              error={daysOfInventoryError}
              helpText="Days after the arrival date"
              inputMode="numeric"
              label="Days of inventory"
              max={365}
              min={1}
              onChange={(value: string) => {
                handleDaysOfInventoryChange(Number(value));
              }}
              suffix="Days"
              type="integer"
              value={daysOfInventory?.toString()}
            />
          </div>
          <BlockStack>
            {selectedForecastingMethod === "seasonal" ? (
                <Labelled id="target-stockout-date" label="Sampling period">
                  <Box padding="100" paddingInline="0">
                    <Tooltip content={`${daysOfInventory} days of inventory after your estimated arrival date of ${humanDateFormat(arrivalDate)}, last year`} hasUnderline>
                    <span>{humanDateFormat(samplingPeriodStartLastYear)}</span> - <span>{humanDateFormat(samplingPeriodEndLastYear)}</span>
                    </Tooltip>
                  </Box>
                  <Box padding="200" paddingInline="0">
                    <Text as="p" tone="subdued" variant="bodyMd">
                    Horse will recommend the amount of inventory that was sold during this period last year.
                    </Text>
                  </Box>
                </Labelled>
            ) : (
                <Labelled id="target-stockout-date" label="Target stockout date">
                  <Box padding="100" paddingInline="0">
                    <Tooltip content={`${daysOfInventory} days of inventory after your estimated arrival date of ${humanDateFormat(arrivalDate)}`} hasUnderline>
                      <span>{humanDateFormat(stockoutDate)}</span>
                    </Tooltip>
                  </Box>
                  <Box padding="200" paddingInline="0">
                    <Text as="p" tone="subdued" variant="bodyMd">
                      Horse will recommend enough inventory to last until this date.
                    </Text>
                  </Box>
                </Labelled>
            )}
          </BlockStack>
        </InlineStack>

        <BlockStack>
          <Button
            icon={ChevronDownIcon}
            onClick={() => {
              setCollapsibleOpen(!collapsibleOpen);
            }}
            textAlign="start"
            variant="plain"
          >
            {collapsibleOpen ? "Hide" : "Advanced options"}
          </Button>
          <Collapsible id="recommendations-collapsible" open={collapsibleOpen}>
            <BlockStack gap="400">
              <Labelled
                helpText="Only use sales from certain sales channels."
                id="select-sales-sources"
                label="Select sales channels (optional)"
              >
                <PopoverWithCheckList
                  choices={salesShopifyChannels}
                  defaultTitle="Sales channels"
                  onChange={handleSalesSourceSelection}
                  onValueRemoved={removeShopifyChannel}
                  selected={salesChannelIds.map((id) => id.toString())}
                />
              </Labelled>
              <Checkbox
                checked={includeAllOutOfStockVariants}
                helpText="Recommend all variants that are out of stock, regardless of sales."
                label="Include all out of stock variants"
                onChange={handleIncludeAllOutOfStockVariantsChange}
              />
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </FormLayout>
    </LegacyStack>
  );
}

export default ForecastingForm;
